import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { PageHeader, DataTable, StatusBadge, FormDialog, Field, Loading } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AreaPicker } from "@/components/AreaPicker";

const EMPTY = { nama: "", singkatan: "", kontak: "", area: "A1", status: "Aktif", targetStbBulananTon: 0 };

export default function MasterData() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => { api.get("/vendors").then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e))); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      if (edit) { await api.patch(`/vendors/${edit.id}`, form); toast.success("Vendor diperbarui / 供应商已更新"); }
      else { await api.post("/vendors", form); toast.success("Vendor ditambahkan / 供应商已创建"); }
      setOpen(false); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Hapus vendor ${r.nama}?`)) return;
    try { await api.delete(`/vendors/${r.id}`); toast.success("Vendor dihapus"); load(); } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-5">
      <PageHeader id="Master Data Vendor" zh="主数据 · 供应商" desc="Data vendor subkon & area kerja fabrikasi."
        actions={<Button className="bg-sky-600 hover:bg-sky-500" onClick={() => { setEdit(null); setForm(EMPTY); setOpen(true); }} data-testid="vendor-add-button"><Plus className="mr-1.5 h-4 w-4" />Tambah Vendor</Button>} />

      <div className="panel p-4" data-testid="vendors-panel">
        {!rows ? <Loading /> : (
          <DataTable testid="vendors-table" rows={rows} columns={[
            { key: "no", label: "No", render: (r, i) => <span className="font-mono text-slate-500">{i + 1}</span> },
            { key: "nama", label: "Nama Vendor", render: (r) => r.nama },
            { key: "singkatan", label: "Singkatan", render: (r) => <span className="rounded border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 font-mono text-xs text-indigo-300" data-testid={`vendor-singkatan-${r.nama}`}>{r.singkatan || "—"}</span> },
            { key: "kontak", label: "Kontak", render: (r) => <span className="font-mono text-slate-400 text-xs">{r.kontak || "—"}</span> },
            { key: "area", label: "Area", render: (r) => <span className="rounded border border-slate-700 px-2 py-0.5 font-mono text-xs text-slate-300">{r.area}</span> },
            { key: "targetStbBulananTon", label: "Target STB/bln", render: (r) => <span className="font-mono text-xs text-amber-300" data-testid={`vendor-target-${r.nama}`}>{r.targetStbBulananTon ? `${r.targetStbBulananTon} T` : "—"}</span> },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
            { key: "aksi", label: "Aksi", render: (r) => (
              <span className="flex gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => { setEdit(r); setForm({ nama: r.nama, singkatan: r.singkatan || "", kontak: r.kontak || "", area: r.area, status: r.status, targetStbBulananTon: r.targetStbBulananTon || 0 }); setOpen(true); }} data-testid={`vendor-edit-${r.nama}`}><Pencil className="h-3.5 w-3.5 text-slate-400" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(r)} data-testid={`vendor-delete-${r.nama}`}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>
              </span>
            )},
          ]} />
        )}
      </div>

      <FormDialog open={open} onOpenChange={setOpen} testid="vendor-dialog"
        title={edit ? `Edit Vendor · ${edit.nama}` : "Tambah Vendor / 新建供应商"} onSubmit={submit} submitLabel={edit ? "Simpan" : "Tambah"}>
        <Field label="Nama Vendor"><Input required value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="CV KARYA BAJA" data-testid="vendor-nama-input" /></Field>
        <Field label="Singkatan (nama di grafik)"><Input value={form.singkatan} onChange={(e) => setForm({ ...form, singkatan: e.target.value.toUpperCase() })} placeholder="CKB" maxLength={12} data-testid="vendor-singkatan-input" /></Field>
        <Field label="Kontak"><Input value={form.kontak} onChange={(e) => setForm({ ...form, kontak: e.target.value })} placeholder="email / no. telp" data-testid="vendor-kontak-input" /></Field>
        <Field label="Area — pilih dari daftar atau tambah area baru secara manual">
          <AreaPicker testid="vendor-area" value={form.area} onChange={(v) => setForm({ ...form, area: v })} extra={(rows || []).map((r) => r.area)} />
        </Field>
        <Field label="Target STB Bulanan (Ton) — vendor di bawah target ditandai merah di grafik">
          <Input type="number" min="0" step="0.1" value={form.targetStbBulananTon} onChange={(e) => setForm({ ...form, targetStbBulananTon: Number(e.target.value) })} placeholder="0 = tanpa target" data-testid="vendor-target-input" />
        </Field>
        <Field label="Status">
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} data-testid="vendor-status-select">
            <SelectTrigger className="border-slate-800 bg-slate-900/70"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0D1524] border-slate-800">
              <SelectItem value="Aktif">Aktif</SelectItem>
              <SelectItem value="Draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </FormDialog>
    </div>
  );
}